import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';

import { Flex, SortHeader, IconBadge, Image, DashPlaceholder, Text, PropertiesTable, formatDisplayDate } from 'dash-ui';
import { PropertyStatusesEnum } from 'dash-common';

import { RoutesPath } from 'flow-components/routes/routes';
import { BadgePropertyStatusChange } from 'containers/properties/components/BadgePropertyStatusChange';
import usePropertiesData from 'common/hooks/usePropertiesData';
import { GetPropertyByIdQuery } from 'gql/generated.graphql';

import type { ICMSPropertiesTableProps } from './CMSPropertiesTable.interfaces';

const NameColumnFlex = styled(Flex)`
  cursor: pointer;
`;

const StyledIconBadge = styled(IconBadge)`
  :hover {
    background-color: ${({ theme }) => theme.colors.g20};
    border: 1px solid ${({ theme }) => theme.colors.g20};
    color: ${({ theme }) => theme.colors.white};
  }
`;

export const CMSPropertiesTable: FC<ICMSPropertiesTableProps> = ({
  hideColumns,
  actionProps,
  ...tableProps
}) => {
  const { t } = useTranslation('tables', {
    keyPrefix: 'properties',
  });
  const { t: tLabels } = useTranslation('common', { keyPrefix: 'labels' });

  const { data, loading: loadingProperties } = usePropertiesData();

  const navigate = useNavigate();

  const columns = useMemo(
    () =>
      [
        {
          Header: () => <SortHeader accessor="name">{t`nameLabel`}</SortHeader>,
          accessor: 'name',
          name: 'name',
          Cell: ({
            value,
            row,
          }: {
            value: string;
            row: { original: GetPropertyByIdQuery['getPropertyById'] };
          }) => {
            const file = row.original.mainPropertyMediaFile?.files[0];

            return (
              <NameColumnFlex
                alignItems="center"
                onClick={() =>
                  navigate(
                    RoutesPath.PropertyDetails.replace(':id', row.original._id)
                  )
                }
              >
                {file ? (
                  <Image
                    mr={2}
                    src={file.thumbnailFile?.publicUrl || ''}
                    fit="contain"
                  />
                ) : (
                  <DashPlaceholder mr={2} />
                )}
                {value}
              </NameColumnFlex>
            );
          },
        },
        {
          Header: () => (
            <SortHeader accessor="primaryOrganization.name">{tLabels`organization`}</SortHeader>
          ),
          accessor: 'primaryOrganization.name',
          name: 'primaryOrganization',
        },
        {
          Header: () => (
            <SortHeader accessor="updatedAt">{t`updatedAtLabel`}</SortHeader>
          ),
          accessor: 'updatedAt',
          Cell: ({ value }: { value: Date }) =>
            formatDisplayDate(value),
          name: 'updatedAt',
        },
        {
          Header: () => (
            <SortHeader accessor="status">{tLabels`status`}</SortHeader>
          ),
          accessor: 'status',
          name: 'status',
          Cell: ({ value }: { value: PropertyStatusesEnum }) => (
            <BadgePropertyStatusChange currentStatus={value} />
          ),
        },
        {
          Header: () => (
            <SortHeader accessor="note">{tLabels`notes`}</SortHeader>
          ),
          accessor: 'note',
          name: 'note',
          Cell: ({ row }: { row: { original: { _id: string } } }) => (
            <Link
              target="_self"
              to={`${RoutesPath.Properties}/${row.original._id}?tab=NOTES`}
            >
              <StyledIconBadge small icon="note" variation="primary" />
            </Link>
          ),
        },
        {
          Header: () => <Text whiteSpace="nowrap">{tLabels`preview`}</Text>,
          accessor: '_id',
          Cell: ({ row }: { row: { original: { _id: string } } }) => (
            <Link
              to={RoutesPath.PropertyPreview.replace(':id', row.original._id)}
              target="_blank"
            >
              <StyledIconBadge small icon="show" variation="primary" />
            </Link>
          ),
          name: 'preview',
        },
      ].filter(({ name }) => !hideColumns?.includes(name)),
    [t, navigate, tLabels, hideColumns]
  );

  return (
    <PropertiesTable
      actionProps={actionProps}
      columns={columns}
      data={data}
      loading={loadingProperties}
      {...tableProps}
    />
  );
};

export default CMSPropertiesTable;
